import  { useState } from 'react';
import { Brain, Heart, Shield, Star, Clock, CheckCircle, ArrowRight, Phone, Calendar, MessageCircle, Target, Lightbulb } from 'lucide-react';
import { SEO } from '../../SEO';

const MentalWellness = () => {
  const [selectedProgram, setSelectedProgram] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('all');

  const programs = [
    {
      id: 1,
      title: "Mindfulness Practice",
      category: "meditation",
      duration: "8 Weeks",
      sessions: "16 Sessions",
      price: "₹4,999",
      originalPrice: "₹6,999",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      rating: 4.9,
      reviews: 245,
      color: "bg-purple-500",
      icon: <Brain className="w-6 h-6" />,
      description: "Develop mindful awareness and inner peace through guided meditation, breathing techniques, and present-moment practices.",
      benefits: ["Stress Reduction", "Better Focus", "Emotional Balance", "Improved Sleep"],
      techniques: ["Guided Meditation", "Breathing Exercises", "Body Scan", "Walking Meditation"],
      includes: ["Weekly Group Sessions", "Daily Practice Materials", "Progress Tracking", "Expert Guidance"]
    },
    {
      id: 2,
      title: "Mental Clarity",
      category: "cognitive",
      duration: "6 Weeks",
      sessions: "12 Sessions",
      price: "₹3,799",
      originalPrice: "₹4,999",
      image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      rating: 4.8,
      reviews: 189,
      color: "bg-blue-500",
      icon: <Lightbulb className="w-6 h-6" />,
      description: "Enhance cognitive function, memory, and mental sharpness through structured brain training exercises.",
      benefits: ["Enhanced Memory", "Better Decision Making", "Increased Productivity", "Mental Agility"],
      techniques: ["Cognitive Training", "Memory Exercises", "Problem Solving", "Focus Techniques"],
      includes: ["Brain Training Apps", "Weekly Assessments", "Personalized Plans", "Progress Reports"]
    },
    {
      id: 3,
      title: "Stress & Anxiety Relief",
      category: "therapy",
      duration: "10 Weeks",
      sessions: "20 Sessions",
      price: "₹6,499",
      originalPrice: "₹8,999",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      rating: 4.9,
      reviews: 312,
      color: "bg-green-500",
      icon: <Shield className="w-6 h-6" />,
      description: "Comprehensive program to manage stress and anxiety through evidence-based therapeutic approaches.",
      benefits: ["Stress Management", "Anxiety Reduction", "Coping Strategies", "Emotional Resilience"],
      techniques: ["CBT Techniques", "Relaxation Methods", "Exposure Therapy", "Mindfulness"],
      includes: ["Individual Sessions", "Group Support", "Crisis Support", "Take-home Resources"]
    },
    {
      id: 4,
      title: "Emotional Resilience",
      category: "wellbeing",
      duration: "12 Weeks",
      sessions: "24 Sessions",
      price: "₹7,999",
      originalPrice: "₹10,999",
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      rating: 4.7,
      reviews: 156,
      color: "bg-red-500",
      icon: <Heart className="w-6 h-6" />,
      description: "Build emotional strength and resilience to handle life's challenges with confidence and grace.",
      benefits: ["Emotional Stability", "Better Relationships", "Self-Confidence", "Life Balance"],
      techniques: ["Emotional Intelligence", "Relationship Skills", "Self-Care Practices", "Goal Setting"],
      includes: ["Life Coaching", "Personality Assessment", "Action Plans", "Ongoing Support"]
    }
  ];

  const therapyApproaches = [
    {
      title: "Cognitive Behavioral Therapy",
      description: "Evidence-based approach focusing on changing negative thought patterns and behaviors.",
      icon: <Brain className="w-8 h-8 text-blue-500" />,
      techniques: ["Thought Restructuring", "Behavioral Activation", "Exposure Therapy"]
    },
    {
      title: "Mindfulness-Based Therapy",
      description: "Integration of mindfulness practices with therapeutic techniques for holistic healing.",
      icon: <Target className="w-8 h-8 text-purple-500" />,
      techniques: ["MBSR", "MBCT", "ACT", "DBT Skills"]
    },
    {
      title: "Positive Psychology",
      description: "Focus on strengths, resilience, and positive emotions to enhance overall well-being.",
      icon: <Star className="w-8 h-8 text-yellow-500" />,
      techniques: ["Gratitude Practice", "Strengths Identification", "Flow States"]
    },
    {
      title: "Holistic Wellness",
      description: "Comprehensive approach integrating mind, body, and spirit for complete wellness.",
      icon: <Heart className="w-8 h-8 text-teal-500" />,
      techniques: ["Yoga Therapy", "Meditation", "Nutrition Counseling"]
    }
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      age: 28,
      location: "Mumbai",
      program: "Stress & Anxiety Relief",
      rating: 5,
      text: "The stress management techniques completely transformed my daily life. I'm more productive, less overwhelmed, and actually enjoy work again.",
      image: "https://images.unsplash.com/photo-1494790108755-2616b2fc6e4d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
    },
    {
      name: "Arjun Patel",
      age: 35,
      location: "Delhi",
      program: "Mental Clarity",
      rating: 5,
      text: "My focus and decision-making abilities have improved dramatically. The cognitive exercises really work - I feel sharper and more confident.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
    },
    {
      name: "Meera Joshi",
      age: 42,
      location: "Bangalore",
      program: "Mindfulness Practice",
      rating: 5,
      text: "Learning mindfulness has been life-changing. I'm calmer, more present with my family, and handle challenges with much more grace.",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
    }
  ];

  const filteredPrograms = activeTab === 'all' 
    ? programs 
    : programs.filter(program => program.category === activeTab);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Mental Wellness, Counseling & Stress Management | MetaFit Wellness"
        description="Nurture your mental well-being with evidence-based counseling, mindfulness practices, and stress management programs at MetaFit Wellness."
        canonical="/mental-wellness"
        keywords="mental wellness, counseling, stress relief, anxiety management, mindfulness meditation, cognitive therapy, MetaFit Wellness"
      />

      {/* Hero Section */}
      <div className="relative h-96 bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800">
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-center w-full">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              MetaFit Mental <span className="text-purple-300">Wellness</span>
            </h1>
            <p className="text-xl text-white mb-8 max-w-3xl mx-auto">
              Transform your emotional well-being with evidence-based psychology, mindfulness, and personalized therapy approaches designed for lasting mental health.
            </p>
            <div className="flex justify-center space-x-4">
            </div>
          </div>
        </div>
      </div>

    
      {/* Programs Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 font-bold">
        <div className="text-center">
          <p className="text-2xl font-semibold text-gray-600">Coming Soon</p>
        </div>
      </div>

      {/* Our Approach Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Approach to Mental Wellness</h2>
            <p className="text-lg text-gray-600">We believe in a holistic approach to mental wellness, integrating various therapeutic methods to support your journey.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {therapyApproaches.map((approach, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow">
                <div className="mb-4 flex justify-center">{approach.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{approach.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{approach.description}</p>
                <div className="space-y-1">
                  {approach.techniques.map((technique, idx) => (
                    <div key={idx} className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                      {technique}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

    

      {/* Contact Options */}
      <div className="bg-purple-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Transform Your Mental Well-being?</h2>
            <p className="text-xl text-purple-100">Choose how you'd like to get started with our mental wellness programs</p>
          </div>

          <div className="flex justify-center">
            <div className="bg-white rounded-2xl p-8 text-center max-w-md w-full">
              <Phone className="w-12 h-12 text-purple-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Call Us Now</h3>
              <p className="text-gray-600 mb-6">Speak directly with our mental wellness team</p>
              <button className="w-full bg-purple-500 text-white py-3 rounded-lg font-semibold hover:bg-purple-600 transition-colors">
                Call +919982498555
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Program Details Modal */}
      {selectedProgram && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="relative">
              <img src={selectedProgram?.image} alt={selectedProgram?.title} className="w-full h-64 object-cover rounded-t-2xl" />
              <button 
                onClick={() => setSelectedProgram(null)}
                className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-100 text-xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="p-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className={`${selectedProgram?.color} p-3 rounded-lg text-white`}>
                  {selectedProgram?.icon}
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900">{selectedProgram?.title}</h3>
                  <div className="flex items-center space-x-4 text-gray-600">
                    <span>{selectedProgram?.duration}</span>
                    <span>•</span>
                    <span>{selectedProgram?.sessions}</span>
                  </div>
                </div>
              </div>
              
              <p className="text-lg text-gray-600 mb-8">{selectedProgram?.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Key Benefits</h4>
                  <ul className="space-y-2">
                    {selectedProgram?.benefits?.map((benefit: string, index: number) => (
                      <li key={index} className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="text-gray-600">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Techniques Used</h4>
                  <ul className="space-y-2">
                    {selectedProgram?.techniques?.map((technique: string, index: number) => (
                      <li key={index} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <span className="text-gray-600">{technique}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="mb-8">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Program Includes</h4>
                <div className="grid grid-cols-2 gap-4">
                  {selectedProgram?.includes?.map((item: string, index: number) => (
                    <div key={index} className="flex items-center space-x-2 bg-gray-50 p-3 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-purple-500" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-6 border-t">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-3xl font-bold text-purple-600">{selectedProgram?.price}</span>
                    <span className="text-xl text-gray-400 line-through">{selectedProgram?.originalPrice}</span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">30% OFF</span>
                  </div>
                  <div className="text-sm text-gray-600">⭐ {selectedProgram?.rating} ({selectedProgram?.reviews} reviews)</div>
                </div>
                <div className="flex space-x-4">
                  <button className="border border-purple-500 text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 transition-colors">
                    Free Trial
                  </button>
                  <button className="bg-purple-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-600 transition-colors">
                    Enroll Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentalWellness;